// Rule applied: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slidePanel", "slidePanelContent", "date", "loading", "datePicker"]
  
  connect() {
    // Initialize the controller
    document.addEventListener("turbo:submit-end", this.handleFormSubmission.bind(this))
    
    // Add resize event listener to handle responsive behavior
    window.addEventListener("resize", this.handleResize.bind(this))
  }
  
  disconnect() {
    // Clean up event listeners when controller is disconnected
    document.removeEventListener("turbo:submit-end", this.handleFormSubmission.bind(this))
    window.removeEventListener("resize", this.handleResize.bind(this))
  }
  
  // Handle window resize for responsive behavior
  handleResize() {
    if (this.hasSlidePanelTarget && !this.slidePanelTarget.classList.contains('translate-x-full')) {
      // Adjust panel width based on screen size
      if (window.innerWidth < 640) { // sm breakpoint in Tailwind
        this.slidePanelTarget.classList.add('w-full')
        this.slidePanelTarget.classList.remove('w-96')
      } else {
        this.slidePanelTarget.classList.remove('w-full')
        this.slidePanelTarget.classList.add('w-96')
      }
    }
  }
  
  // Calendar navigation methods
  goToToday(event) {
    event.preventDefault()
    const today = new Date()
    const formattedDate = today.toISOString().split('T')[0]
    
    // Update the date picker
    if (this.hasDatePickerTarget) {
      this.datePickerTarget.value = formattedDate
    }
    
    // Reload the page with today's date
    window.location.href = `/admin/dashboard?date=${formattedDate}`
  }
  
  previousMonth(event) {
    event.preventDefault()
    const currentDate = new Date()
    currentDate.setMonth(currentDate.getMonth() - 1)
    const formattedDate = currentDate.toISOString().split('T')[0]
    
    // Reload the page with the previous month
    window.location.href = `/admin/dashboard?date=${formattedDate}`
  }
  
  nextMonth(event) {
    event.preventDefault()
    const currentDate = new Date()
    currentDate.setMonth(currentDate.getMonth() + 1)
    const formattedDate = currentDate.toISOString().split('T')[0]
    
    // Reload the page with the next month
    window.location.href = `/admin/dashboard?date=${formattedDate}`
  }
  
  selectMonth(event) {
    event.preventDefault()
    // Rule applied: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
    
    // Get the month number directly from the data attribute (1-12)
    const monthNumber = parseInt(event.currentTarget.dataset.monthNumber, 10)
    const month = event.currentTarget.dataset.month
    
    // Get the current date to maintain the year
    const currentDate = new Date()
    // Set the day to the 1st to avoid month overflow issues
    // JavaScript months are 0-indexed, so subtract 1 from the month number
    const targetDate = new Date(currentDate.getFullYear(), monthNumber - 1, 1)
    const formattedDate = targetDate.toISOString().split('T')[0]
    
    console.log("Calendar: Selecting month:", month, "(month number:", monthNumber, ") formatted as:", formattedDate)
    
    // Reload the page with the selected month
    window.location.href = `/admin/dashboard?date=${formattedDate}`
  }
  
  dateSelected(event) {
    const selectedDate = event.target.value
    
    // Reload the page with the selected date
    window.location.href = `/admin/dashboard?date=${selectedDate}`
  }
  
  showDayDetails(event) {
    event.preventDefault()
    
    // Get the date from the clicked element's data attribute
    const date = event.currentTarget.dataset.date
    console.log("Calendar: Date clicked:", date)
    
    // Check if the clicked date is the same as the currently selected date
    // If it is, close the panel instead of reopening it
    if (this.selectedDate === date && this.hasSlidePanelTarget && 
        !this.slidePanelTarget.classList.contains('translate-x-full')) {
      console.log("Calendar: Closing panel - same date clicked")
      this.close()
      return
    }
    
    // Update the date display in the slide panel
    if (this.hasDateTarget) {
      // Create date with timezone handling to avoid date shifting
      // Use the parts of the date string to create a date at noon on the specified day
      // This avoids timezone issues that can shift the date
      const [year, month, day] = date.split('-').map(num => parseInt(num, 10))
      const displayDate = new Date(year, month - 1, day, 12, 0, 0)
      console.log("Calendar: Display date object:", displayDate)
      
      this.dateTarget.textContent = displayDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      console.log("Calendar: Formatted date for display:", this.dateTarget.textContent)
      
      // Store the selected date for future reference
      this.selectedDate = date
    }
    
    // Show loading state
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
    
    // Show the slide panel
    if (this.hasSlidePanelTarget) {
      // Make panel visible before animation
      this.slidePanelTarget.style.visibility = 'visible'
      
      // Lock body scroll
      document.body.classList.add('overflow-hidden')
      
      // Handle responsive sizing
      this.handleResize()
      
      // Animate panel in
      requestAnimationFrame(() => {
        this.slidePanelTarget.classList.remove('translate-x-full')
        this.slidePanelTarget.classList.add('translate-x-0')
      })
    }
    
    // Fetch the reservation details for this date
    this.fetchReservationDetails(date)
  }
  
  fetchReservationDetails(date) {
    // Rule applied: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
    // Fix timezone issues by ensuring we use the date as is without timezone conversion
    
    // The date from the data attribute is in YYYY-MM-DD format
    // We need to pass it directly to the server without any conversion
    // that might cause timezone issues
    console.log("Calendar: Fetching details for date:", date)
    
    fetch(`/admin/reservations/day_details?date=${date}`, {
      headers: {
        Accept: "text/html, application/xhtml+xml",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(response => response.text())
    .then(html => {
      // Update the slide panel content with the fetched HTML
      if (this.hasSlidePanelContentTarget) {
        this.slidePanelContentTarget.innerHTML = html
      }
      
      // Hide loading state
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }
    })
    .catch(error => {
      console.error("Error fetching reservation details:", error)
      
      // Hide loading state
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }
      
      // Show error message
      if (this.hasSlidePanelContentTarget) {
        this.slidePanelContentTarget.innerHTML = `
          <div class="p-4 text-center">
            <p class="text-red-500 dark:text-red-400">
              Error loading reservation details. Please try again.
            </p>
          </div>
        `
      }
    })
  }
  
  // Handle form submissions (check-in/check-out)
  handleFormSubmission(event) {
    // Rule applied: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
    // Rule applied: Handle errors gracefully in controllers
    
    // Only handle successful form submissions
    if (event.detail?.success) {
      // Get the selected date from the date target
      const selectedDate = this.selectedDate
      
      // Refresh the reservation details after a short delay
      // to allow Turbo Stream updates to complete
      setTimeout(() => {
        if (selectedDate) {
          this.fetchReservationDetails(selectedDate)
        }
      }, 300)
    }
  }
  
  close(event) {
    // Rule applied: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
    if (event) {
      event.preventDefault()
    }
    
    if (this.hasSlidePanelTarget) {
      // Animate panel out
      this.slidePanelTarget.classList.remove('translate-x-0')
      this.slidePanelTarget.classList.add('translate-x-full')
      
      // Hide panel after animation completes
      setTimeout(() => {
        this.slidePanelTarget.style.visibility = 'hidden'
        
        // Clear the content after hiding
        if (this.hasSlidePanelContentTarget) {
          this.slidePanelContentTarget.innerHTML = ''
        }
        
        // Reset selected date
        this.selectedDate = null
      }, 300)
      
      // Unlock body scroll
      document.body.classList.remove('overflow-hidden')
    }
  }
  
  // Close the slide panel when pressing Escape key
  keydown(event) {
    // Rule applied: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
    if (event.key === 'Escape') {
      this.close()
    }
  }
}
